import { normalize } from 'normalizr';
import { mutation, query, chimeraMutation } from '@/dex-shared/helpers/graphql';
import {
  essayReply,
  question,
  text,
  content,
} from '@/dex-shared/helpers/normalizr_schema';
import { fileUploadURL } from '@/dex-shared/settings/endpoints';

export default {
  async PULL_DRAFTS({ commit, rootState }: any) {
    let response = await query(
      `query DraftsByUserId($userId: Int!) {
      allEssayReplies(condition: {
        userId: $userId
        status: "DRAFT"
      }) {
        nodes {
          id
          status
          position
          updatedAt
          contentsByEssayReplyId {
            nodes {
              id
              position
              essayReplyId
              textByTextId {
                id
                source
                body
                alignment
              }
              imageByImageId {
                id
                url
                source
                resizeFactor
                description
                alt
              }
              videoByVideoId {
                id
                providerType
                providerId
                source
              }
            }
          }
          questionByQuestionId {
            id
            title
            slug
            examModeByExamModeId {
              id
              name
            }
          }
        }
      }
    }`,
      {
        userId: +rootState.auth.user.id,
      },
    );
    let normalizedResponse = normalize(response.data.allEssayReplies, {
      nodes: [essayReply],
    });
    commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
  },
  async PULL_ESSAY(
    { commit, dispatch, rootState }: any,
    { slug, position }: any,
  ) {
    let response = await query(
      `query GetEssay($slug: String!, $userId: Int!, $position: Int!) {
      questionBySlug(slug: $slug) {
        id
        title
        slug
        createdAt
        examModeByExamModeId {
          id
          name
        }
        contentsByQuestionId {
          nodes {
            id
            questionId
            position
            textByTextId {
              id
              source
              body
              alignment
            }
            imageByImageId {
              id
              url
              source
              resizeFactor
              description
              alt
            }
            videoByVideoId {
              id
              providerType
              providerId
              source
            }
          }
        }
        essayRepliesByQuestionId(condition: {
          userId: $userId
          position: $position
        }) {
          nodes {
            id
            userId
            questionId
            status
            position
            contentsByEssayReplyId {
              nodes {
                id
                position
                essayReplyId
                textByTextId {
                  id
                  source
                  body
                  alignment
                }
                imageByImageId {
                  id
                  url
                  source
                  resizeFactor
                  description
                  alt
                }
                videoByVideoId {
                  id
                  providerType
                  providerId
                  source
                }
              }
            }
            correctionsByEssayReplyId {
              nodes {
                id
                score
                finalizedAt
                comment
                examModeByExamModeId {
                  id
                  name
                  maxScore
                }
                userByCorrectorUserId {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }`,
      {
        userId: +rootState.auth.user.id,
        slug: slug,
        position: +position,
      },
    );
    let normalizedResponse = normalize(response.data.questionBySlug, question);
    if (!normalizedResponse.entities.essay_replies) {
      dispatch('CREATE_DRAFT', {
        questionId: response.data.questionBySlug.id,
        textBody: '',
      });
    }
    commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
  },
  async PUSH_ESSAY({ commit, getters }: any, essayReplyId: number) {
    try {
      const chimeraResponse = await chimeraMutation(
        `mutation SendEssayToCorrector ($essayReplyId: Int!) {
          chimera {
            sendEssayToCorrector(dexEssayReplyId: $essayReplyId) {
              id: dexEssayReplyId
            }
          }
        }`,
        { essayReplyId },
      );

      const dexResponse = await query(
        `
          query PullEssayReplyById ($essayReplyId: Int!) {
            essayReplyById(id: $essayReplyId) {
              id
              status
              position
              updatedAt
              contentsByEssayReplyId {
                nodes {
                  id
                  position
                  essayReplyId
                  textByTextId {
                    id
                    source
                    body
                    alignment
                  }
                  imageByImageId {
                    id
                    url
                    source
                    resizeFactor
                    description
                    alt
                  }
                  videoByVideoId {
                    id
                    providerType
                    providerId
                    source
                  }
                }
              }
              questionByQuestionId {
                id
                slug
              }
            }
          }
        `,
        { essayReplyId: chimeraResponse.data.chimera.sendEssayToCorrector.id },
      );

      const essayCreditsCurrentBalance = getters.userEssayCredits;
      const updatedEssayCreditsBalance = essayCreditsCurrentBalance - 1;
      commit('SET_USER_ESSAY_CREDITS', updatedEssayCreditsBalance);
      const normalizedResponse = normalize(
        dexResponse.data.essayReplyById,
        essayReply,
      );
      commit('SET_NORMALIZED_DATA', normalizedResponse.entities);
    } catch (error) {
      console.log(error.message);
    }
  },
  async UPDATE_DRAFT({ getters }: any, { subjectSlug, position }: any) {
    let currentEssay = getters.essayByPosition(subjectSlug, position);
    if (currentEssay.textAnswer && currentEssay.textAnswer().raw) {
      let response = await mutation(
        `mutation UpdateDraft($id: Int!, $body: String!) {
          updateTextById(
            input: {
              id: $id
              textPatch: {
                body: $body
              }
            }
          ) {
            text {
              id
            }
          }
        }`,
        currentEssay.textAnswer().raw(),
      );
    }
  },
  async CREATE_DRAFT(
    { commit, rootState }: any,
    { questionId, textBody }: any,
  ) {
    let response = await mutation(
      `mutation CreateDraft($questionId: Int!, $userId: Int!, $essayBody: String!) {
        createText(
          input: {
            text: {
              userId: $userId
              body: $essayBody
            }
          }
        ) {
          text {
            id
            userId
            body
          }
        }
        createEssayReply(
          input: {
            essayReply: {
              questionId: $questionId
              userId: $userId
              status: "DRAFT"
            }
          }
        ) {
          essayReply {
            id
            questionId
            userId
            status
            position
          }
        }
      }`,
      {
        questionId: +questionId,
        userId: +rootState.auth.user.id,
        essayBody: textBody,
      },
    );

    commit('UPDATE_ENTITY', {
      entity: 'essay_replies',
      data: response.data.createEssayReply.essayReply,
    });
    commit('UPDATE_ENTITY', {
      entity: 'texts',
      data: response.data.createText.text,
    });

    let contentResponse = await mutation(
      `mutation CreateDraftAssociation($textId: Int! $userId: Int!, $essayReplyId: Int!) {
        createContent(input: {
          content: {
            textId: $textId
            userId: $userId
            position: 0
            essayReplyId: $essayReplyId
          }
        }) {
          content {
            id
            userId
            position
            essayReplyId
            textId
          }
        }
      }`,
      {
        textId: response.data.createText.text.id,
        userId: +rootState.auth.user.id,
        essayReplyId: response.data.createEssayReply.essayReply.id,
      },
    );

    let normalizedContent = normalize(
      contentResponse.data.createContent.content,
      content,
    );
    commit('SET_NORMALIZED_DATA', normalizedContent.entities);
  },
  async CREATE_AND_ASSOCIATE_IMAGE(
    { commit, rootState }: any,
    { url, essayId }: any,
  ) {
    let userId = rootState.auth.user.id;
    let imageResponse = await mutation(
      `mutation CreateImage($userId: Int!, $url: String!) {
        createImage(input: {
          image: {
            userId: $userId
            url: $url
          }
        }) {
          image {
            id
            userId
            url
          }
        }
      }`,
      {
        userId,
        url,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'images',
      data: imageResponse.data.createImage.image,
    });
    let imageId = imageResponse.data.createImage.image.id;
    let contentResponse = await mutation(
      `mutation CreateContent($imageId: Int!, $essayId: Int!, $userId: Int!) {
        createContent(input: {
          content: {
            imageId: $imageId
            essayReplyId: $essayId
            userId: $userId
          }
        }) {
          content {
            id
            imageId
            essayReplyId
            userId
          }
        }
      }`,
      {
        imageId,
        essayId,
        userId,
      },
    );
    commit('UPDATE_ENTITY', {
      entity: 'contents',
      data: contentResponse.data.createContent.content,
    });
  },
  async UPLOAD_IMAGE(_: any, data: string) {
    let response = await fetch(fileUploadURL, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({ image: data }),
    });
    let { location } = await response.json();
    return location;
  },
};
